import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Contact } from '../../../models/Contact';
import { ContactRestDataProvider } from '../../../data/ContactRestDataProvider';
import TextInput from '../../widgets/TextInput';
import { FaPlus, FaSave, FaTimes, FaTrash } from 'react-icons/fa';
import Toolbar, { ToolbarButtonConfig, ToolbarFilterConfig } from '../../common/Toolbar/Toolbar';
import './ContactsTab.scss';
import { useTranslation } from 'react-i18next';

interface ContactsTabProps {
    parentId: string;
    parentType: string;
}

const emptyContact = (parentId: string, parentType: string): Contact => ({
    id: '',
    parentId,
    parentType,
    firstName: '',
    lastName: '',
    jobTitle: '',
    phoneNumber: '',
    mobileNumber: '',
    emailAddress: '',
    notes: '',
});

const ContactsTab: React.FC<ContactsTabProps> = ({ parentId, parentType }) => {
    const { t } = useTranslation(['common', 'screens']);
    const provider = useMemo(() => new ContactRestDataProvider(), []);
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [selected, setSelected] = useState<Contact | null>(null);
    const [form, setForm] = useState<Contact | null>(null);
    const [saving, setSaving] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const load = useCallback(async () => {
        const data = await provider.getByParentId(parentId);
        setContacts(data);
    }, [parentId]);

    useEffect(() => {
        load();
        setSelected(null);
        setForm(null);
        setShowDeleteConfirm(false);
    }, [parentId, load]);

    const selectContact = (c: Contact) => {
        setSelected(c);
        setForm({ ...c });
        setShowDeleteConfirm(false);
    };

    const handleNew = () => {
        setSelected(null);
        setForm(emptyContact(parentId, parentType));
        setShowDeleteConfirm(false);
    };

    const set = <K extends keyof Contact>(field: K, value: Contact[K]) =>
        setForm(prev => prev ? { ...prev, [field]: value } : prev);

    const handleSave = async () => {
        if (!form) return;
        setSaving(true);
        try {
            if (!form.id) {
                const created = await provider.add({ ...form, id: crypto.randomUUID() });
                setContacts(prev => [...prev, created]);
                setSelected(created);
                setForm({ ...created });
            } else {
                await provider.update(form.id, form);
                setContacts(prev => prev.map(c => c.id === form.id ? form : c));
                setSelected(form);
            }
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!selected) return;
        await provider.delete(selected.id);
        setContacts(prev => prev.filter(c => c.id !== selected.id));
        setSelected(null);
        setForm(null);
        setShowDeleteConfirm(false);
    };

    const isNew = !form?.id;

    const displayName = (c: Contact) =>
        [c.firstName, c.lastName].filter(Boolean).join(' ') || '(unnamed)';

    // ── Toolbar buttons / filters ──────────────────────────────────────────────
    const toolbarFilters: ToolbarFilterConfig[] = [];
    const toolbarButtons: ToolbarButtonConfig[] = [];

    if (showDeleteConfirm) {
        toolbarFilters.push({
            key: 'delete-confirm-text',
            type: 'custom',
            render: () => (
                <span className="contacts-tab__confirm-text">{t('common:areYouSure')}</span>
            ),
        });
        toolbarButtons.push(
            { key: 'confirm-delete', icon: <FaTrash />, label: t('common:yesDelete'), variant: 'danger',    onClick: handleDelete },
            { key: 'cancel-delete',  icon: <FaTimes />, label: t('common:cancel'),    variant: 'secondary', onClick: () => setShowDeleteConfirm(false) },
        );
    } else {
        toolbarButtons.push({
            key: 'add', icon: <FaPlus />, label: t('screens:contacts.addContact'), variant: 'primary', onClick: handleNew,
        });
        if (form && !isNew) {
            toolbarButtons.push({
                key: 'delete', icon: <FaTrash />, label: t('common:delete'), variant: 'danger', onClick: () => setShowDeleteConfirm(true),
            });
        }
        if (form) {
            toolbarButtons.push(
                { key: 'cancel', icon: <FaTimes />, label: t('common:cancel'),                          variant: 'secondary', onClick: () => { setForm(null); setSelected(null); } },
                { key: 'save',   icon: <FaSave />,  label: isNew ? t('common:create') : t('common:save'), variant: 'primary',   onClick: handleSave, disabled: saving },
            );
        }
    }

    return (
        <div className="contacts-tab">
            {/* ── Toolbar ── */}
            <Toolbar
                buttons={toolbarButtons}
                filters={toolbarFilters}
                className="contacts-tab__toolbar"
            />

            {/* ── Panels row ── */}
            <div className="contacts-tab__panels">
                {/* Left: list panel */}
                <div className="contacts-tab__list-panel">
                    <div className="contacts-tab__list">
                        {contacts.map(c => (
                            <button
                                key={c.id}
                                type="button"
                                className={`contacts-tab__list-item${selected?.id === c.id ? ' contacts-tab__list-item--active' : ''}`}
                                onClick={() => selectContact(c)}
                            >
                                <span className="contacts-tab__list-name">{displayName(c)}</span>
                                {c.jobTitle && <span className="contacts-tab__list-role">{c.jobTitle}</span>}
                            </button>
                        ))}
                        {contacts.length === 0 && (
                            <div className="contacts-tab__list-empty">{t('screens:contacts.noContacts')}</div>
                        )}
                    </div>
                </div>

                {/* Right: detail panel */}
                <div className="contacts-tab__detail">
                    {!form ? (
                        <div className="contacts-tab__empty">
                            {t('screens:contacts.emptyHint')}
                        </div>
                    ) : (
                        <div className="contacts-tab__detail-inner">
                            <div className="contacts-tab__detail-scroll">

                                {/* Section: Name */}
                                <div className="contacts-tab__section">
                                    <div className="contacts-tab__section-title">{t('screens:contacts.sections.name')}</div>
                                    <div className="contacts-tab__row">
                                        <div className="contacts-tab__col">
                                            <TextInput
                                                label={t('screens:contacts.fields.firstName')}
                                                value={form.firstName ?? ''}
                                                onChange={v => set('firstName', v)}
                                            />
                                        </div>
                                        <div className="contacts-tab__col">
                                            <TextInput
                                                label={t('screens:contacts.fields.lastName')}
                                                value={form.lastName ?? ''}
                                                onChange={v => set('lastName', v)}
                                            />
                                        </div>
                                        <div className="contacts-tab__col">
                                            <TextInput
                                                label={t('screens:contacts.fields.jobTitle')}
                                                value={form.jobTitle ?? ''}
                                                onChange={v => set('jobTitle', v)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Section: Contact */}
                                <div className="contacts-tab__section">
                                    <div className="contacts-tab__section-title">{t('screens:contacts.sections.contact')}</div>
                                    <div className="contacts-tab__row">
                                        <div className="contacts-tab__col">
                                            <TextInput
                                                label={t('screens:contacts.fields.phoneNumber')}
                                                value={form.phoneNumber ?? ''}
                                                onChange={v => set('phoneNumber', v)}
                                            />
                                        </div>
                                        <div className="contacts-tab__col">
                                            <TextInput
                                                label={t('screens:contacts.fields.mobileNumber')}
                                                value={form.mobileNumber ?? ''}
                                                onChange={v => set('mobileNumber', v)}
                                            />
                                        </div>
                                        <div className="contacts-tab__col contacts-tab__col--wide">
                                            <TextInput
                                                label={t('screens:contacts.fields.emailAddress')}
                                                value={form.emailAddress ?? ''}
                                                onChange={v => set('emailAddress', v)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Section: Notes */}
                                <div className="contacts-tab__section">
                                    <div className="contacts-tab__section-title">{t('screens:contacts.sections.notes')}</div>
                                    <textarea
                                        className="form-control"
                                        rows={3}
                                        value={form.notes ?? ''}
                                        onChange={e => set('notes', e.target.value)}
                                        placeholder={t('screens:contacts.fields.notes')}
                                    />
                                </div>

                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ContactsTab;
